import React, { useCallback, useState } from 'react';
import { IFaviconUrlSource, SourceFavicon } from './SourceFavicon';
import { LOGO_TILE_BACKGROUND, initialsOf, tileInkFor } from './BrandLandscapeCard.utils';

/**
 * A brand's mark: its logo where one exists, and its initials on the colour its tag facet gives it
 * everywhere else.
 *
 * Which of the two is showing is decided by the RENDERED RESULT, never by whether a domain is known.
 * A domain is only a lead: the icon behind it may still be queued, may not exist, or may 404, and
 * branching on the domain alone would leave a blank square in exactly the cases the reader most
 * needs a mark. So the coloured square with the initials is what the tile starts as, and it is
 * dropped only once `SourceFavicon` reports an icon actually on the screen — at which point the
 * colour would be a rim around a logo that already carries the brand's identity, and two marks
 * competing to say the same thing.
 *
 * The mark always sits on a ground of its own — the facet colour, or white under a logo. That is
 * also what tells it apart from whatever is behind it, so a logo tile needs no white ring: a ring is
 * the same white square drawn two pixels wider, and one drawn around a transparent tile is a hairline
 * outline of a shape the reader cannot see the edges of.
 *
 * Decorative throughout: every caller renders the brand's name beside it or carries the name in the
 * label of the region around it, so an assistive reader that announced the initials too would hear
 * the brand named twice.
 */
export function BrandLandscapeTile({
  api,
  name,
  color,
  size,
  domain,
  requestSize,
  overlapping,
}: {
  api?: IFaviconUrlSource;
  name: string;
  color: string;
  size: number;
  domain?: string;
  /** The one icon size to ask Sync Core for, whatever size this mark is drawn at. */
  requestSize?: number;
  /**
   * This mark is drawn over another one, so it carries a rim the eye can follow and a stack reads as
   * a stack rather than as one brand. Owned here rather than passed in as a class, because it is the
   * same decision as which ground the mark sits on.
   */
  overlapping?: boolean;
}) {
  /**
   * The domain whose logo is on the screen, rather than a bare "loaded".
   *
   * Held this way so that a tile whose brand changes under it cannot keep drawing as though the
   * previous brand's logo were still there: the answer is only trusted while it is an answer about
   * the domain currently being asked for.
   */
  const [loadedDomain, setLoadedDomain] = useState<string | undefined>(undefined);
  const showsLogo = !!domain && loadedDomain === domain;

  // Memoized on the domain: `SourceFavicon` is a memo, and a fresh closure every render would
  // re-render it — and with it every tile — on each pass over the strip and the plot.
  const onLoadedChange = useCallback((loaded: boolean) => setLoadedDomain(loaded ? domain : undefined), [domain]);

  const rim = overlapping ? 'ring-2 ring-slate-900/40' : showsLogo ? '' : 'ring-2 ring-white';

  return (
    <span
      aria-hidden
      data-brand-mark
      className={`relative inline-flex items-center justify-center overflow-hidden rounded ${rim}`}
      style={{ width: size, height: size, backgroundColor: showsLogo ? LOGO_TILE_BACKGROUND : color, color: tileInkFor(color) }}
    >
      {showsLogo ? null : (
        <span className="font-semibold leading-none" style={{ fontSize: Math.max(8, Math.round(size * 0.42)) }}>
          {initialsOf(name)}
        </span>
      )}
      {domain ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <SourceFavicon api={api} domain={domain} size={size} requestSize={requestSize} onLoadedChange={onLoadedChange} />
        </span>
      ) : null}
    </span>
  );
}
